import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { portalApi } from '../../lib/parceiroApi';
import { useParceiroAuth } from '../../contexts/ParceiroAuthContext';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ParceiroPerfil() {
  const { user, setUser, parceiro, logout } = useParceiroAuth();
  const [params] = useSearchParams();
  const [limite, setLimite] = useState(null);
  const [valorAumento, setValorAumento] = useState('');
  const [motivoAumento, setMotivoAumento] = useState('');
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [busy, setBusy] = useState(false);

  const loadLimite = useCallback(() => {
    portalApi.limite().then((r) => setLimite(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadLimite(); }, [loadLimite]);

  const solicitarAumento = async () => {
    setBusy(true);
    try {
      await portalApi.solicitarAumento(parseFloat(valorAumento), motivoAumento);
      toast.success('Solicitação de aumento enviada!');
      setValorAumento(''); setMotivoAumento('');
      loadLimite();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao solicitar');
    } finally { setBusy(false); }
  };

  const alterarSenha = async () => {
    setBusy(true);
    try {
      await portalApi.alterarSenha(senhaAtual.trim(), novaSenha.trim());
      toast.success('Senha alterada com sucesso');
      setSenhaAtual(''); setNovaSenha('');
      if (user?.senha_temporaria) setUser({ ...user, senha_temporaria: false });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao alterar senha');
    } finally { setBusy(false); }
  };

  const destacarSenha = params.get('trocar_senha') === '1' || user?.senha_temporaria;
  const destacarAumento = params.get('aumento') === '1';

  return (
    <div className="max-w-xl mx-auto space-y-4" data-testid="parceiro-perfil">
      <h1 className="text-xl font-bold text-[#3D4F5F]">Meu perfil</h1>

      <Card>
        <CardContent className="p-4 text-sm space-y-1">
          <div className="font-bold text-[#3D4F5F]">{user?.nome}</div>
          <div className="text-gray-500">{user?.email} {user?.cargo ? `· ${user.cargo}` : ''}</div>
          <div className="text-gray-500">Parceiro: <b>{parceiro?.nome}</b> ({parceiro?.codigo})</div>
          <div className="text-xs text-gray-400">Permissão: {user?.permissao === 'admin_parceiro' ? 'Administrador do parceiro' : 'Solicitante'}</div>
        </CardContent>
      </Card>

      <Card className={destacarAumento ? 'border-[#2B7A3D]' : ''}>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Limite de crédito</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 space-y-3">
          {!limite ? <Loader2 className="animate-spin text-gray-400" /> : (
            <>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div><div className="text-xs text-gray-500">Total</div><b>{formatCurrency(limite.total)}</b></div>
                <div><div className="text-xs text-gray-500">Utilizado</div><b className="text-amber-600">{formatCurrency(limite.utilizado)}</b></div>
                <div><div className="text-xs text-gray-500">Disponível</div><b className="text-[#2B7A3D]">{formatCurrency(limite.disponivel)}</b></div>
              </div>
              {limite.solicitacao_pendente ? (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 rounded-lg p-2.5 text-xs" data-testid="aumento-pendente">
                  Você já tem um pedido de aumento pendente ({formatCurrency(limite.solicitacao_pendente.valor_solicitado)}). A Tenance está analisando.
                </div>
              ) : (
                <div className="space-y-2 border-t border-gray-100 pt-3">
                  <div className="text-xs font-semibold text-[#3D4F5F]">Solicitar aumento de limite</div>
                  <div><Label className="text-xs">Novo limite desejado (R$)</Label>
                    <Input type="number" min="0" value={valorAumento} onChange={(e) => setValorAumento(e.target.value)} placeholder={`maior que ${limite.total}`} data-testid="input-valor-aumento" /></div>
                  <div><Label className="text-xs">Motivo</Label>
                    <Textarea rows={2} value={motivoAumento} onChange={(e) => setMotivoAumento(e.target.value)} placeholder="ex: Aumento no volume de manutenções dos imóveis" data-testid="input-motivo-aumento" /></div>
                  <Button className="bg-[#2B7A3D] hover:bg-[#236632] w-full" disabled={busy || !valorAumento} onClick={solicitarAumento} data-testid="btn-enviar-aumento">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : 'Solicitar aumento'}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card className={destacarSenha ? 'border-amber-400' : ''}>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Alterar senha</CardTitle></CardHeader>
        <CardContent className="p-4 pt-0 space-y-2">
          {user?.senha_temporaria && <p className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2">Você está usando uma senha temporária. Defina uma nova senha.</p>}
          <div><Label className="text-xs">Senha atual</Label><Input type="password" value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} data-testid="input-senha-atual" /></div>
          <div><Label className="text-xs">Nova senha (mín. 6 caracteres)</Label><Input type="password" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} data-testid="input-nova-senha" /></div>
          <Button variant="outline" className="w-full" disabled={busy || !senhaAtual || novaSenha.length < 6} onClick={alterarSenha} data-testid="btn-alterar-senha">Alterar senha</Button>
        </CardContent>
      </Card>

      <Button variant="ghost" className="w-full text-red-500" onClick={logout} data-testid="btn-sair-perfil">Sair da conta</Button>
    </div>
  );
}
